import React from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { getActiveRides, ActiveRide } from '../services/liveOpsService';
import PageHelp from '../components/common/PageHelp';

const statusColor = (s: string): any => {
  switch (s) {
    case 'requested': return 'warning';
    case 'accepted': return 'info';
    case 'arrived': return 'secondary';
    case 'in_progress': return 'success';
    default: return 'default';
  }
};

/**
 * Live Ops — read-only monitor of in-flight rides. Drivers accept rides
 * themselves; this screen is purely for visibility (spotting stuck/long rides).
 * Surge is managed per-zone on the Zones page, not here.
 */
const LiveOps: React.FC = () => {
  const rides = useQuery({
    queryKey: ['active-rides'],
    queryFn: getActiveRides,
    refetchInterval: 8000,
  });

  const list: ActiveRide[] = rides.data?.data ?? [];

  return (
    <Box sx={{ width: '100%', maxWidth: 1500, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <SensorsIcon color="success" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Live Ops</Typography>
        <PageHelp k="liveops" />
        <Chip size="small" label={`${list.length} active`} color="success" variant="outlined" />
        <Box flex={1} />
        <Tooltip title="Refresh now">
          <IconButton onClick={() => rides.refetch()}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        In-flight rides, auto-refreshing every 8s. Read-only — drivers accept rides themselves.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Ride</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pickup</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Dropoff</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rider</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rides.isLoading && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>
            )}
            {!rides.isLoading && list.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>No active rides right now.</TableCell></TableRow>
            )}
            {list.map((ride: ActiveRide) => (
              <TableRow key={ride.id} hover>
                <TableCell>#{ride.id}</TableCell>
                <TableCell><Chip size="small" label={ride.status} color={statusColor(ride.status)} /></TableCell>
                <TableCell>{ride.type ?? '-'}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ride.pickupLocation}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ride.dropoffLocation}</TableCell>
                <TableCell>{ride.rider?.phone ?? ride.riderId}</TableCell>
                <TableCell>{ride.driver?.phone ?? (ride.driverId ? ride.driverId : '—')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{ride.requestedAt ? format(new Date(ride.requestedAt), 'HH:mm:ss') : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LiveOps;
